import {
  Component,
  OnInit,
  DestroyRef,
  inject,
  signal,
} from "@angular/core";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexDataLabels,
  ApexPlotOptions,
  ApexGrid,
  ApexTooltip,
  ApexStroke,
  ApexFill,
} from 'ng-apexcharts';
import { ActivatedRoute } from "@angular/router";
import { Router } from "@angular/router";
// Services
import { GetSectionSummaryService } from "../../../data/section/get-section-summary.service";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { ListSectionMonthlySummariesService } from "../../../data/section/list-section-monthly-summaries.service";

import { HttpParams } from "@angular/common/http";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { ADMIN_HOME_I18N } from "./home.i18n";


@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  imports: SHARED_IMPORTS,
})
export class AdminHomeComponent implements OnInit {
  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = ADMIN_HOME_I18N;

  isLoading = signal(true);
  readonly currentYear = new Date().getFullYear();

  // section
  selectedSection: any;
  sectionIdInUrl;

  error: any;
  promise = Promise.resolve();

  xAxisUnit = "month";
  period = "12";

  responseData: any = [];

  // Number of Accounts
  numOfStaffs = 0;
  numOfContributors = 0;
  numOfUsers = 0;

  // Quota Info
  currentUsageGB = 0;
  quotaLimitGB = 100; // Default quota limit
  remainingGB = 0;
  usagePercentage = 0;

  // Trends (month over month)
  staffTrend = 0;
  contributorTrend = 0;
  userTrend = 0;

  // Previous month values for trend calculation
  private prevNumOfStaffs = 0;
  private prevNumOfContributors = 0;
  private prevNumOfUsers = 0;

  // Notices
  notices: { date: string; text: string; link?: string[] }[] = [
    { date: '2026/05/30', text: '大幅アップデートを実施しました。' },
    { date: '2025/03/24', text: '「月次サマリー」から月間のデータ使用量をご確認いただけるようになりました。' },
    { date: '2025/02/07', text: '連絡帳をご利用の方向けに通知機能を追加しました。' },
    { date: '2025/02/07', text: 'ユーザーサマリーをご利用の方向けのPDF出力にGOKURIレコードを追加できるように修正しました。' },
    { date: '2024/09/25', text: 'ユーザーサマリーをご利用の方向けのPDF出力のデザインを修正しました。' },
    { date: '2024/09/09', text: 'GOST機能をアップデートしました。各レコード表示UIの修正をしました。' },
    { date: '2024/08/02', text: 'ユーザーリストのUIの修正を行い、最終更新日でソートできるように変更しました。' },
    { date: '2023/09/26', text: 'ユーザーサマリーをご利用の方向けに、日付選択やデータ修正機能を実装しました。' },
    { date: '2023/09/01', text: 'GOKURIクラウドサービスのバージョンアップを行い、UIを変更ました。' },
    { date: '2023/02/20', text: 'GOKURIクラウドサービスのバージョンアップを行いました。' },
    { date: '2022/10/08', text: 'GOKURIアプリのバージョンアップを行いました。' },
  ];

  // Number of Accounts: Month to Month Basis
  createdAccountNumMTMB = 0;
  // createdAccountNumMTMBsign = -1;
  createdAccountNumMTMBsign = true;

  removedAccountNumMTMB = 0;
  // removedAccountNumMTMBsign = -1;
  removedAccountNumMTMBsign = true;

  totalAccountNumMTMB = 0;
  totalAccountNumMTMBsign = true;

  periodOfUseApiRes: any = [];
  monthlySummariesDataSource: any = [];

  // RSST
  rsstInfoMoreThanThree = 0;
  rsstInfoLessThanThree = 0;

  // ApexCharts configuration
  chartSeries: ApexAxisChartSeries = [{ name: ADMIN_HOME_I18N.chart.yAxisDataUsage, data: [] }];
  chartOptions: ApexChart = {
    type: 'bar',
    height: 300,
    foreColor: '#757575',
    toolbar: { show: false },
    animations: { enabled: true, speed: 500 },
  };
  chartXAxis: ApexXAxis = {
    categories: [],
    title: { text: ADMIN_HOME_I18N.chart.xAxisMonth, style: { color: '#757575', fontSize: '12px' } },
    labels: { style: { colors: '#757575', fontSize: '11px' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  };
  chartYAxis: ApexYAxis = {
    min: 0,
    title: { text: ADMIN_HOME_I18N.chart.yAxisDataUsage, style: { color: '#757575', fontSize: '12px' } },
    labels: {
      style: { colors: '#757575', fontSize: '11px' },
      formatter: (val: number) => val.toFixed(1),
    },
  };
  chartDataLabels: ApexDataLabels = { enabled: false };
  chartPlotOptions: ApexPlotOptions = {
    bar: { borderRadius: 5, columnWidth: '55%' },
  };
  chartStroke: ApexStroke = {
    show: true,
    width: 2,
    colors: ['transparent'],
  };
  chartFill: ApexFill = { opacity: 0.88 };
  chartGrid: ApexGrid = {
    borderColor: '#E0E0E0',
    strokeDashArray: 1,
    xaxis: { lines: { show: false } },
  };
  chartTooltip: ApexTooltip = {
    theme: 'dark',
    marker: { show: true },
    y: { formatter: (val: number) => `${val.toFixed(2)} GB` },
  };
  chartColors = ['#6750A4'];

  isHandset$: Observable<boolean>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutBreakpoint = inject(LayoutBreakpointService);

  constructor(
    private getSectionSummaryService: GetSectionSummaryService,
    private router: Router,
    public common: DatabaseCacheService,
    private route: ActivatedRoute,
    private listSectionMonthlySummariesService: ListSectionMonthlySummariesService,
  ) {}

  ngOnInit() {
    this.isHandset$ = this.layoutBreakpoint.isHandset$;

    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((noData) => {
      // Sectionに関わるデータの読み込み
      this.selectedSection = this.common.getSelectedSection();
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      //   this.getSectionSummary();

      this.sectionIdInUrl = params.get("sectionId"); // sectionId in the URL pass
      if (this.sectionIdInUrl != null) {
        this.common.setSelectedSectionById(this.sectionIdInUrl);
        this.getMonthlySummaries(this.sectionIdInUrl);
      } else {
        this.common.setSelectedSectionById(
          this.common.getSelectedSection().sectionId,
        );
      }

      // Sectionに関わるデータの読み込み
      this.selectedSection = this.common.getSelectedSection();
    });
  }

  private updateChartData(categories: (string | number)[], data: number[]): void {
    this.chartXAxis = { ...this.chartXAxis, categories };
    this.chartSeries = [{ name: ADMIN_HOME_I18N.chart.yAxisDataUsage, data }];
  }

  getSectionSummary() {
    this.error = "";

    this.getSectionSummaryService
      .getSectionSummary(this.xAxisUnit, this.period)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          if (Object.keys(res).indexOf("error") !== -1) {
            this.error = (res as any).error;
          } else {
            this.responseData = res;
            this.periodOfUseApiRes = (res as any).deviceUsageTrend;

            // Device Usage Trend
            const xLabels: string[] = [];
            const yValues: number[] = [];
            for (let i = 0; i < (res as any).deviceUsageTrend.length; i++) {
              xLabels.push((res as any).deviceUsageTrend[i].x);
              yValues.push((res as any).deviceUsageTrend[i].y);
            }

            // Number of Accounts: Month to Month Basis
            this.createdAccountNumMTMB =
              (res as any).deviceUsageInfo.createdAccountThisMonth -
              (res as any).deviceUsageInfo.createdAccountLastMonth;
            if (this.createdAccountNumMTMB > 0) {
              this.createdAccountNumMTMBsign = true;
            } else if (this.createdAccountNumMTMB < 0) {
              this.createdAccountNumMTMBsign = false;
            }
            this.removedAccountNumMTMB =
              (res as any).deviceUsageInfo.deletedAccountThisMonth -
              (res as any).deviceUsageInfo.deletedAccountLastMonth;
            if (this.removedAccountNumMTMB > 0) {
              this.removedAccountNumMTMBsign = true;
            } else if (this.removedAccountNumMTMB < 0) {
              this.removedAccountNumMTMBsign = false;
            }
            this.totalAccountNumMTMB =
              (res as any).deviceUsageInfo.createdAccountThisMonth -
              (res as any).deviceUsageInfo.deletedAccountThisMonth;
            if (this.totalAccountNumMTMB > 0) {
              this.totalAccountNumMTMBsign = true;
            } else if (this.totalAccountNumMTMB < 0) {
              this.totalAccountNumMTMBsign = false;
            }

            this.rsstInfoMoreThanThree = (res as any).rsstInfo.moreThanThree;
            this.rsstInfoLessThanThree = (res as any).rsstInfo.lessThanThree;

            this.updateChartData(xLabels, yValues);
          }
        },
        (error) => {
        },
      );
  }

  getMonthlySummaries(sectionId) {
    let year = new Date().getFullYear();

    let params: HttpParams = new HttpParams()
      .set("fiscalYearFrom", year)
      .set("fiscalMonthFrom", 1)
      .set("fiscalYearTo", year)
      .set("fiscalMonthTo", 12);

    params = params.set("sectionIds", sectionId);

    this.isLoading.set(true);
    this.listSectionMonthlySummariesService
      .listSectionMonthlySummaries(params)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe((res) => {

        let monthlySummaries = res as any;
        this.monthlySummariesDataSource = [];
        const chartData: number[] = [];
        monthlySummaries.forEach((s) => {
          let section = s.sections[0];
          const gb = section.dataUsedBytes / 1024 / 1024 / 1024;
          this.monthlySummariesDataSource.push({ x: section.fiscalMonth, y: gb });
          chartData.push(gb);
        });

        this.updateChartData(
          [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          chartData,
        );

        if (monthlySummaries.length > 0) {
          const lastMonthSummary =
            monthlySummaries[monthlySummaries.length - 1];
          const lastSection = lastMonthSummary.sections[0];

          // Current values
          this.numOfUsers = lastSection.numOfUsers ?? 0;
          this.numOfStaffs = lastSection.numOfStaffs ?? 0;
          this.numOfContributors = lastSection.numOfContributors ?? 0;

          // Calculate current usage in GB
          this.currentUsageGB = (lastSection.dataUsedBytes ?? 0) / 1024 / 1024 / 1024;
          this.quotaLimitGB = (lastSection.dataQuotaBytes ?? 100 * 1024 * 1024 * 1024) / 1024 / 1024 / 1024;
          this.remainingGB = Math.max(0, this.quotaLimitGB - this.currentUsageGB);
          this.usagePercentage = this.quotaLimitGB > 0
            ? Math.min(100, (this.currentUsageGB / this.quotaLimitGB) * 100)
            : 0;

          // Calculate trends (compare with previous month)
          if (monthlySummaries.length > 1) {
            const prevMonthSummary = monthlySummaries[monthlySummaries.length - 2];
            const prevSection = prevMonthSummary.sections[0];

            this.prevNumOfStaffs = prevSection.numOfStaffs ?? 0;
            this.prevNumOfContributors = prevSection.numOfContributors ?? 0;
            this.prevNumOfUsers = prevSection.numOfUsers ?? 0;

            this.staffTrend = this.numOfStaffs - this.prevNumOfStaffs;
            this.contributorTrend = this.numOfContributors - this.prevNumOfContributors;
            this.userTrend = this.numOfUsers - this.prevNumOfUsers;
          }
        }
      });
  }

  changePeriod(period: any) {
    this.promise
      .then((value) => {
        this.period = period;
      })
      .then((value) => {
        this.getSectionSummary();
      })
      .catch((error) => {
      });
  }

  showMeasuredData(event: any, elements: any, graphType: string) {
    // const activePoints = this.numOfCoughData.getElementsAtEventForMode(event, 'point', this.numOfCoughData.options);
    // const firstPoint = activePoints[0];
    this.router.navigate(["/channels/measured-data-list"], {
      queryParams: { xAxis: elements[0]["_model"].label, graphType: graphType },
    });
  }
}
